import { useCallback } from "react"
import { useMutation } from "urql"

import {
  type CompanyProductAlternativeAssociationCreateInput,
  CreateCompanyProductAlternativeDocument,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCreateAlternativeReturn {
  createAlternative: (input: CompanyProductAlternativeAssociationCreateInput) => Promise<boolean>
  isCreating: boolean
}

export const useCreateAlternative = (): UseCreateAlternativeReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation(CreateCompanyProductAlternativeDocument)

  const createAlternative = useCallback(
    async (input: CompanyProductAlternativeAssociationCreateInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.companyProductAssociation?.createAlternative

      if (error || output?.__typename === "CompanyProductAlternativeAssociationCreateFailure") {
        const message =
          output?.__typename === "CompanyProductAlternativeAssociationCreateFailure"
            ? output.error.message
            : error?.message
        notify({
          type: NotificationType.Error,
          message: message ?? "Failed to add alternative. Please try again.",
        })
        return false
      }

      notify({ type: NotificationType.Success, message: "Alternative added" })
      return true
    },
    [executeMutation, notify]
  )

  return { createAlternative, isCreating: fetching }
}
